import { ActionButton, Button, ConfirmModal, ExportDropdown } from '@practium/ui';
import styles from './ParametersCard.module.css';
import { MacronutrientsTab } from './MacronutrientsTab';
import { MicronutrientsTab } from './MicronutrientsTab';
import { ChemicalIndexesTab } from './ChemicalIndexesTab';
import { useParametersDraft } from '../../../hooks/useParametersDraft';
import type { StateReferenceDTO, ParametersTab, TextureSoil } from '../../../types/parameters.types';

const TABS: { key: ParametersTab; label: string }[] = [
	{ key: 'macronutrients', label: 'Macronutrientes' },
	{ key: 'micronutrients', label: 'Micronutrientes' },
	{ key: 'chemicalIndexes', label: 'Índices Químicos' },
];

interface ParametersCardProps {
	regionName: string;
	stateReference: StateReferenceDTO | null;
	activeTab: ParametersTab;
	selectedTexture: TextureSoil;
	isLoading: boolean;
	onTabChange: (tab: ParametersTab) => void;
	onTextureChange: (texture: TextureSoil) => void;
	onExportExcel: () => void;
	onExportPdf: () => void;
	onUpdate: (updated: StateReferenceDTO) => Promise<void>;
	onAddNew: () => void;
}

export const ParametersCard = ({
	regionName,
	stateReference,
	activeTab,
	selectedTexture,
	isLoading,
	onTabChange,
	onTextureChange,
	onExportExcel,
	onExportPdf,
	onUpdate,
	onAddNew,
}: ParametersCardProps) => {
	const {
		draft,
		localTexture,
		isDirty,
		isSaving,
		tabKey,
		handleTextureChange,
		handleDraftDependentParams,
		handleDraftFixedParams,
		handleSave,
		handleCancelEdit,
		confirmModal,
	} = useParametersDraft({ stateReference, selectedTexture, onUpdate, onTextureChange });

	return (
		<>
			<div className={styles.card}>
				<div className={styles.header}>
					<div className={styles.headerLeft}>
						{isLoading && !stateReference ? (
							<div className={styles.skeletonTitle} />
						) : (
							<>
								<h2 className={styles.title}>{regionName}</h2>
								<span className={styles.subtitle}>
									Parâmetros de interpretação de análise de solo configurados
								</span>
							</>
						)}
					</div>
					<div className={styles.actionButtons}>
						<ActionButton text="Adicionar" onClick={onAddNew} />
						<ExportDropdown
							onExportExcel={onExportExcel}
							onExportPdf={onExportPdf}
							buttonClassName={styles.exportBtn}
						/>
					</div>
				</div>

				<div className={styles.tabsWrapper}>
					{TABS.map((tab) => (
						<button
							key={tab.key}
							className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
							onClick={() => onTabChange(tab.key)}
						>
							{tab.label}
						</button>
					))}
				</div>

				<div className={styles.tabContent}>
					{activeTab === 'macronutrients' && (
						<MacronutrientsTab
							key={tabKey}
							params={draft?.dependentParams ?? []}
							texture={localTexture}
							isLoading={isLoading}
							onTextureChange={handleTextureChange}
							onChange={handleDraftDependentParams}
						/>
					)}
					{activeTab === 'micronutrients' && (
						<MicronutrientsTab
							key={tabKey}
							fixedParams={draft?.fixedParams ?? []}
							isLoading={isLoading}
							onChange={handleDraftFixedParams}
						/>
					)}
					{activeTab === 'chemicalIndexes' && (
						<ChemicalIndexesTab
							key={tabKey}
							fixedParams={draft?.fixedParams ?? []}
							isLoading={isLoading}
							onChange={handleDraftFixedParams}
						/>
					)}
				</div>

				{isDirty && (
					<div className={styles.footer}>
						<div className={styles.unsavedIndicator}>
							<span className={styles.unsavedDot} />
							<span className={styles.unsavedText}>Alterações não salvas</span>
						</div>
						<div className={styles.footerActions}>
							<Button
								variant="secondary"
								className={styles.footerBtn}
								onClick={handleCancelEdit}
								disabled={isSaving}
							>
								Cancelar
							</Button>
							<Button
								variant="primary"
								className={styles.footerBtn}
								onClick={handleSave}
								disabled={isSaving}
							>
								{isSaving ? 'Salvando...' : 'Salvar'}
							</Button>
						</div>
					</div>
				)}
			</div>

			<ConfirmModal
				isOpen={confirmModal.isOpen}
				title={confirmModal.options.title}
				message={confirmModal.options.message}
				confirmText={confirmModal.options.confirmText}
				cancelText={confirmModal.options.cancelText}
				variant={confirmModal.options.variant}
				onConfirm={confirmModal.handleConfirm}
				onCancel={confirmModal.handleCancel}
			/>
		</>
	);
};
